/**
 * byo-v4-giftship.js — reads from Giftship, and nothing else.
 *
 * GIFTSHIP-API.md §1–§3: `info` and `steps` on the configured builder are the
 * only two endpoints this build touches, both GET, both read-only — no
 * Shopify config changes, no builder-id split surfaced in the UI. Fetch once
 * per session, cache in sessionStorage keyed by builder id and a version
 * string so a Giftship-side change during a long session is picked up next
 * visit rather than stuck forever.
 *
 * Every export returns a plain `{ ok, ... }` result rather than throwing —
 * GIFTSHIP-API.md §3: "every fetch needs a real failure path... never a blank
 * grid and never a spinner that outlives the request." A failed `steps` call
 * means Personalise cannot be trusted; callers must block Add To Cart on it
 * rather than submit a half-built payload.
 *
 * ES2020 module, no transpilation, no bundler.
 */

const CACHE_VERSION = 'v1';

function cacheKey(builderId, endpoint) {
  return `byo-v4-giftship:${CACHE_VERSION}:${builderId}:${endpoint}`;
}

function readCache(builderId, endpoint) {
  try {
    const raw = window.sessionStorage.getItem(cacheKey(builderId, endpoint));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(builderId, endpoint, data) {
  try {
    window.sessionStorage.setItem(cacheKey(builderId, endpoint), JSON.stringify(data));
  } catch {
    // sessionStorage can be full or unavailable (private mode) — caching is an
    // optimisation, not a requirement, so a failed write changes nothing.
  }
}

// One in-flight fetch per (builderId, endpoint) — a fast re-render must not
// fire the same request twice.
const inFlight = new Map();

async function fetchJson(builderId, endpoint) {
  const cached = readCache(builderId, endpoint);
  if (cached) return { ok: true, data: cached, cached: true };

  const key = `${builderId}:${endpoint}`;
  if (inFlight.has(key)) return inFlight.get(key);

  const url = `/a/gs/builder/app/${builderId}/${endpoint}`;
  const promise = fetch(url, { headers: { Accept: 'application/json' } })
    .then(async (res) => {
      if (!res.ok) {
        return { ok: false, error: `${endpoint} responded ${res.status}` };
      }
      let data;
      try {
        data = await res.json();
      } catch {
        return { ok: false, error: `${endpoint} did not return valid JSON` };
      }
      writeCache(builderId, endpoint, data);
      return { ok: true, data, cached: false };
    })
    .catch((error) => ({ ok: false, error: error.message || `${endpoint} request failed` }))
    .finally(() => inFlight.delete(key));

  inFlight.set(key, promise);
  return promise;
}

/** Box sizes, base variant, step order. */
export function fetchInfo(builderId) {
  return fetchJson(builderId, 'info');
}

/** Every step with its type and fields — wraps, paid extras, form fields. */
export function fetchSteps(builderId) {
  return fetchJson(builderId, 'steps');
}

/**
 * Giftship returns variant ids as GIDs ("gid://shopify/ProductVariant/557...")
 * in `steps` but as bare numbers in `info`. The cart needs the number.
 */
function gidToId(value) {
  if (value == null) return null;
  const match = String(value).match(/(\d+)\s*$/);
  return match ? Number(match[1]) : null;
}

/**
 * Prices arrive as decimal strings ("2.50"). Everything downstream is integer
 * pence — SPIKE-FINDINGS.md C7: sending pounds priced a £62 box at £0.62.
 */
function poundsToPence(value) {
  if (value == null || value === '') return null;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * `info` → the box-size facts. `min_products` / `max_products` are strings in
 * the response and numbers here; `base_price` becomes pence.
 *
 * OPEN-QUESTIONS.md #7: `/info`'s base_price is the source of truth for what
 * the customer will actually be charged, and the theme's own `base_price`
 * setting is display plus fallback. `checkBasePrice()` below is the "fail
 * loudly in QA when they disagree" half of that resolution.
 */
export function parseInfo(infoResponse) {
  const builder = infoResponse?.builder || {};
  const boxSizes = (builder.box_sizes || []).map((size) => ({
    id: size.id,
    name: size.name,
    basePrice: poundsToPence(size.base_price),
    minProducts: size.min_products == null ? null : Number(size.min_products),
    maxProducts: size.max_products == null ? null : Number(size.max_products),
    variantId: gidToId(size.variant_id),
  }));

  return {
    builderId: builder.id ?? null,
    title: builder.title ?? null,
    productHandle: infoResponse?.product?.handle ?? null,
    boxSizes,
    boxSize: boxSizes[0] || null,
    stepOrder: builder.step_order || [],
  };
}

/**
 * `steps` → the shapes the builder UI actually needs. Built against the real
 * trimmed responses in `reference/byo-builder/sample-data/`, per
 * GIFTSHIP-API.md §1 ("build against those shapes, not against a guess"):
 *
 *   image step   → `images[]` as { imagePath, value } — `value` is the wrap name
 *   product step → `products[]`, each with `variants[0].{id,price}` (GID, decimal string)
 *   form step    → `options[]` as { name, field_type, character_limit, ... }
 *   collection   → `collection.handle`, plus Giftship's own min/max selections
 *
 * A step's **title is the cart property key**, byte-exact — including the
 * trailing space on "2. Choose Your Wrap " (GIFTSHIP-V4-BUILDER.md, verified
 * against 2057 with repr()). They are returned as `stepTitles` so P6 composes
 * the payload from the live response instead of hardcoding strings that would
 * silently diverge from fulfilment.
 *
 * GIFTSHIP-API.md §2: exactly three wraps, paid extras are products, one
 * message per box. Anything the four known types don't cover is a Giftship
 * configuration question, not a parsing one, so the untouched steps stay
 * available under `raw`.
 */
export function parseSteps(stepsResponse) {
  const steps = Array.isArray(stepsResponse) ? stepsResponse : stepsResponse?.steps || [];

  const wraps = [];
  const extras = [];
  const formFields = [];
  const stepTitles = {};
  let collectionStep = null;

  for (const step of steps) {
    if (step.enabled === false) continue;
    stepTitles[step.type] = step.title;

    if (step.type === 'image') {
      for (const image of step.images || []) {
        wraps.push({ id: image.id, name: image.value, img: image.imagePath });
      }
    } else if (step.type === 'product') {
      for (const product of step.products || []) {
        const variant = (product.variants || [])[0] || {};
        extras.push({
          title: product.title,
          handle: product.handle,
          variantId: gidToId(variant.id),
          price: poundsToPence(variant.price),
        });
      }
    } else if (step.type === 'form') {
      for (const field of step.options || []) {
        formFields.push({
          id: field.id,
          name: field.name,
          fieldType: field.field_type,
          label: field.label,
          helpText: field.help_text || '',
          required: !!field.required,
          limit: field.character_limit ?? null,
          /* A radio or select field's options, as plain strings (D72: the
             "how it's given" radio the submit writes into). */
          choices: (Array.isArray(field.choices) ? field.choices : [])
            .map((c) => (typeof c === 'string' ? c : (c && (c.value || c.label || c.name)) || ''))
            .filter(Boolean),
        });
      }
    } else if (step.type === 'collection') {
      collectionStep = {
        handle: step.collection?.handle ?? null,
        title: step.collection?.title ?? null,
        minSelections: step.min_selections == null ? null : Number(step.min_selections),
        maxSelections: step.max_selections == null ? null : Number(step.max_selections),
      };
    }
  }

  return { wraps, extras, formFields, stepTitles, collectionStep, raw: steps };
}

/**
 * The theme's configured base price vs what Giftship will actually charge.
 * Returns null when they agree. Callers surface it in QA — it is a launch
 * blocker, not a runtime error (OPEN-QUESTIONS.md #7, SPIKE-FINDINGS.md C1).
 */
export function checkBasePrice(parsedInfo, configuredBasePence) {
  const live = parsedInfo?.boxSize?.basePrice;
  if (live == null || configuredBasePence == null || live === configuredBasePence) return null;
  return `[byo-v4] base price disagrees: the theme shows ${configuredBasePence}p, Giftship's builder reports ${live}p. The customer is charged the base variant's own price.`;
}

/**
 * Fetch both endpoints for the configured builder and return the parsed
 * shape a caller renders from. Both must succeed — a half-loaded builder
 * (info without steps, or vice versa) is treated as a full failure, per the
 * "block Add To Cart rather than submit a half-built payload" rule above.
 */
export async function loadBuilder(builderId) {
  const [info, steps] = await Promise.all([fetchInfo(builderId), fetchSteps(builderId)]);

  if (!info.ok) return { ok: false, error: info.error };
  if (!steps.ok) return { ok: false, error: steps.error };

  return { ok: true, info: parseInfo(info.data), ...parseSteps(steps.data) };
}

window.BYOGiftship = { fetchInfo, fetchSteps, parseInfo, parseSteps, checkBasePrice, loadBuilder };

export default window.BYOGiftship;
